import { CommonModule } from '@angular/common'
import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { AnimationOptions } from 'ngx-lottie'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { UserService } from 'src/app/service/user-info/user.service'
import { DdcLoadingSkeletonComponent } from 'src/app/shared/ddc/ddc-loading-skeleton/ddc-loading-skeleton.component'
import { Languages, Media } from 'src/app/shared/enums/enums'
import { DdcButtonSOneComponent } from '../../shared/ddc/ddc-button-s-one/ddc-button-s-one.component'
import { DdcElemChooserOneComponent } from '../../shared/ddc/ddc-elem-chooser-one/ddc-elem-chooser-one.component'

@Component({
  selector: 'ddc-setting-profile',
  templateUrl: './ddc-setting-profile.component.html',
  styleUrls: ['./ddc-setting-profile.component.scss'],
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    DdcElemChooserOneComponent,
    DdcButtonSOneComponent,
    ReactiveFormsModule,
    DdcLoadingSkeletonComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcSettingProfileComponent {
  // This is the option that uses the package's AnimationOption interface
  options: AnimationOptions = {
    path: '/assets/lottie/green-tick-6.json',
    loop: false, // Set loop to false for a single playthrough
    autoplay: true,
  }

  profileForm: FormGroup

  _userProfilePic: string
  _DefaultProfilePic: string =
    'https://st3.depositphotos.com/6672868/13701/v/600/depositphotos_137014128-stock-illustration-user-profile-icon.jpg'

  _username: string
  _email: string
  _country: string
  _city: string
  _bio: string

  _favLanguage1: string
  _favLanguage2: string
  _favLanguage3: string

  _languages: { id: number; label: string; rawValue: string }[] = []
  _selectedLanguages: string[] = []
  _selectedLanguagesObj: any[] = []
  _selectedLanguagesLabels: { id: number; label: string; rawValue: string }[] = []
  _selectedLanguagesLabels_test = [{ id: 2, label: 'string', rawValue: 'string' }]

  _loadedFavLanguages: boolean = false
  _followers: number
  _following: number
  _words_count: number
  _pairs_count: number
  _dics_count: number

  _loadedLanguages: boolean = true

  _isUserInfoLoading: boolean = true

  _submitBtnText: string = 'Update'
  _isUpdateBtnLoading: boolean = false
  _isUpdateLoading: boolean = false
  _isUpdated: boolean = false
  _error: string
  _message: string

  // _languages: SelectOption[] = []

  constructor(
    private logger: DdcLoggerService,
    private fb: FormBuilder,
    private user_srv: UserService,
  ) {}

  ngOnInit(): void {
    this.profileForm = this.fb.group({
      // username: ['', [Validators.required, Validators.minLength(3)]],
      // email: ['', [Validators.required, Validators.email]],
      country: ['', [Validators.required, Validators.minLength(2)]],
      city: ['', [Validators.required, Validators.minLength(2)]],
      bio: ['', [Validators.required, Validators.maxLength(500)]],
    })

    this.getLanguages()

    // get user profile infos
    this.user_srv.get_user_profile().subscribe((res: any) => {
      this._userProfilePic = res.profile_pic
      if (this._userProfilePic) {
        this._userProfilePic = Media.ASSETS + this._userProfilePic
      } else {
        this._userProfilePic = this._DefaultProfilePic
      }
      this._isUserInfoLoading = false
      this._email = res.email
      this._username = res.username
      this._country = res.country
      this._city = res.city
      this._bio = res.bio
      this._favLanguage1 = res.favLanguage1
      this._favLanguage2 = res.favLanguage2
      this._favLanguage3 = res.favLanguage3
      this._followers = res.followers.length
      this._following = res.following.length
      this._words_count = res.words_count
      this._pairs_count = res.pairs_count
      this._dics_count = res.dics_count

      // Set initial values for the form
      this.profileForm.patchValue({
        // username: res.username,
        // email: res.email, //TODO: Add email to the backend
        country: res.country,
        city: res.city,
        bio: res.bio,
      })

      // Set other properties
      // this.languages = res.languages; // Assuming the backend provides a list of languages

      this._selectedLanguages = [
        this.getLanguageName(this._favLanguage1),
        this.getLanguageName(this._favLanguage2),
        this.getLanguageName(this._favLanguage3),
      ].filter(Boolean)

      this._selectedLanguagesLabels = this._selectedLanguages.map((language, index) => ({
        id: 0,
        label: language,
        rawValue: [this._favLanguage1, this._favLanguage2, this._favLanguage3][index],
      }))
      this._loadedFavLanguages = true

      // this._selectedLanguagesLabels = this._selectedLanguages.map((language) => ({ id: 0, label: language }));

      this.logger.debug(
        'DdcUserProfileComponent: get_user_profile() | _selectedLanguagesLabels:',
        JSON.stringify(this._selectedLanguagesLabels),
      )
      this.logger.debug(
        'DdcUserProfileComponent: get_user_profile() | _languages:',
        JSON.stringify(this._languages),
      )
    })
  }

  getCountryErrorMessage(): string {
    if (this.profileForm.get('country').hasError('required')) {
      return 'You must enter a country'
    }
    if (this.profileForm.get('country').hasError('minlength')) {
      return 'Country must be at least 2 characters long'
    }
    return ''
  }

  getCityErrorMessage(): string {
    if (this.profileForm.get('city').hasError('required')) {
      return 'You must enter a city'
    }
    if (this.profileForm.get('city').hasError('minlength')) {
      return 'City must be at least 2 characters long'
    }
    return ''
  }

  getBioErrorMessage(): string {
    if (this.profileForm.get('bio').hasError('required')) {
      return 'You must enter a bio'
    }
    if (this.profileForm.get('bio').hasError('maxlength')) {
      return 'Bio cannot be more than 500 characters long'
    }
    return ''
  }

  openProfilePictureDialog(): void {
    // Logic to open the profile picture dialog
  }

  getLanguages(): void {
    this.logger.info('DdcSettingProfileComponent | getLanguages2')

    this._languages = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      id: 0,
      label: viewValue,
      rawValue: rawValue,
    }))
  }

  onSubmit(): void {
    this._isUpdateLoading = true
    if (this.profileForm.valid) {
      // Logic to handle form submission
      this._selectedLanguages = []

      this._selectedLanguagesObj.forEach(language => {
        this._selectedLanguages.push(this.getLanguageCode(language.label))
      })

      // i want to create a new object with the selected languages and city country email and username
      this.profileForm.value.favLanguage1 = this._selectedLanguages[0]
      this.profileForm.value.favLanguage2 = this._selectedLanguages[1]
      this.profileForm.value.favLanguage3 = this._selectedLanguages[2]

      this.updateProfile()
    }
  }

  updateProfile(): void {
    this.logger.info('DdcUserProfileEditDialogComponent: updateProfile()')

    this._submitBtnText = 'Updating...'
    this._isUpdateBtnLoading = true
    this.user_srv.update_user_profile(this.profileForm.value).subscribe((res: any) => {
      if (res.error) {
        this._error = res.error
      } else {
        this._message = res.message + '. Forwarding to profile ...'
        // success
        // this._submitBtnText = 'Updated !'
        this._isUpdateBtnLoading = false
        this._isUpdated = true
        this._message = 'User profile updated successfully !'
      }
      this._isUpdateLoading = false
    })
  }

  removeLanguage(language: string): void {
    const index = this._selectedLanguages.indexOf(language)
    if (index >= 0) {
      this._selectedLanguages.splice(index, 1)
    }
  }

  getLanguageCode(languageName: string): string | null {
    this.logger.info('DdcCountryFlagComponent: getLanguageName() | languageName:', languageName)
    for (const key in Languages) {
      if ((Languages as { [key: string]: readonly string[] })[key][0] === languageName) {
        return (Languages as { [key: string]: readonly string[] })[key][1]
      }
    }
    return null
  }

  getLanguageName(code: string): string | null {
    this.logger.info('DdcCountryFlagComponent: getLanguageName() | code:', code)
    for (const key in Languages) {
      if ((Languages as { [key: string]: readonly string[] })[key][1] === code) {
        return (Languages as { [key: string]: readonly string[] })[key][0]
      }
    }
    return null
  }

  updateSelectedLanguages(languages: { id: number; label: string }[]): void {
    this.logger.info('DdcSettingProfileComponent | updateSelectedLanguages: tags: ' + languages)
    this.logger.debug(
      'DdcSettingProfileComponent | updateSelectedLanguages: tags: ' + JSON.stringify(languages),
    )

    this._selectedLanguagesObj = languages
  }
}
